import { useState, useEffect, useCallback } from 'react';
import type { Notification } from '../contexts/NotificationProvider';

interface UseToastNotificationsOptions {
  notifications: Notification[];
  dismissNotification: (index: number) => void;
}

interface UseToastNotificationsReturn {
  visibleToasts: Notification[];
  handleDismissToast: (id: string) => void;
}

/**
 * Hook to manage toast notification display logic.
 * Tracks which notifications have been shown as toasts to prevent duplicates.
 */
export function useToastNotifications({
  notifications,
  dismissNotification,
}: UseToastNotificationsOptions): UseToastNotificationsReturn {
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);
  const [shownIds, setShownIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Find new notifications that haven't been shown as toasts yet
    const newNotifications = notifications.filter(n =>
      !n.dismissed &&
      n.id &&
      !shownIds.has(n.id)
    );

    if (newNotifications.length > 0) {
      // Add new notifications to visible toasts
      // eslint-disable-next-line react-hooks/set-state-in-effect -- drains newly arrived notifications from context into the visible-toast queue and records which have been shown; the queue has its own dismissals, so it is not derived state
      setVisibleToasts(prev => [...prev, ...newNotifications]);
      // Mark these as shown
      setShownIds(prev => {
        const newSet = new Set(prev);
        newNotifications.forEach(n => n.id && newSet.add(n.id));
        return newSet;
      });
    }
  }, [notifications, shownIds]);

  const handleDismissToast = useCallback((id: string) => {
    setVisibleToasts(prev => prev.filter(t => t.id !== id));
    // Also dismiss from notifications context
    const index = notifications.findIndex(n => n.id === id);
    if (index !== -1) {
      dismissNotification(index);
    }
  }, [notifications, dismissNotification]);

  return {
    visibleToasts,
    handleDismissToast,
  };
}

export default useToastNotifications;
